import React from 'react';
import {
  Person as PersonIcon,
  AccountTree as RelationshipIcon,
  Photo as PhotoIcon,
  Timeline as TimelineIcon,
  History as HistoryIcon,
  Description as DocumentIcon,
  AutoStories as MemoryIcon,
  StickyNote2 as NoteIcon,
  Forum as CommentIcon,
  Science as ResearchIcon,
} from '@mui/icons-material';
import { SectionDef, SectionKey } from './types';

/**
 * Builds section list based on flags, permissions, and counts
 *
 * @param personId - Person ID for route generation
 * @param counts - Badge counts for each section
 * @param flags - Feature flags to enable/disable sections
 * @param perms - Permission gates (future use)
 * @param routePrefix - Optional route prefix (e.g., for subsections)
 * @returns Prioritized array of enabled sections
 */
export function buildSections(
  personId: string,
  counts: Partial<Record<SectionKey, number>> = {},
  flags: Partial<Record<SectionKey, boolean>> = {},
  perms: Partial<Record<SectionKey, boolean>> = {},
  routePrefix?: string
): SectionDef[] {
  // Helper: Check if feature flag is enabled (defaults to true if not specified)
  const isEnabled = (key: SectionKey, defaultValue = true): boolean => {
    const flagValue = flags[key];
    const permValue = perms[key];

    // If flag is explicitly false, disabled
    if (flagValue === false) return false;

    // If perm is explicitly false, disabled
    if (permValue === false) return false;

    // Otherwise use default
    return flagValue ?? permValue ?? defaultValue;
  };

  // Helper: Create section definition
  const createSection = (
    key: SectionKey,
    label: string,
    priority: number,
    icon: React.ReactElement
  ): SectionDef => ({
    key,
    label,
    priority,
    icon,
    route: () => routePrefix
      ? `/people/${personId}/${routePrefix}/${key}`  // Subsection routes: /people/:id/:prefix/:tab
      : `/people/${personId}/${key}`,                // Standard routes: /people/:id/:tab
    enabled: isEnabled(key, true),
    badgeCount: counts[key],
    ariaLabel: counts[key] ? `${label} (${counts[key]})` : label,
  });

  // Define all sections with priorities
  // Lower priority = higher importance = appears first when space limited
  const allSections: SectionDef[] = [
    createSection('overview', 'Overview', 0, React.createElement(PersonIcon)),
    createSection('relationships', 'Relationships', 1, React.createElement(RelationshipIcon)),
    createSection('photos', 'Photos', 2, React.createElement(PhotoIcon)),
    createSection('timeline', 'Timeline', 3, React.createElement(TimelineIcon)),
    createSection('documents', 'Documents', 4, React.createElement(DocumentIcon)),
    createSection('memories', 'Memories', 5, React.createElement(MemoryIcon)),
    createSection('notes', 'Notes', 6, React.createElement(NoteIcon)),
    createSection('comments', 'Comments', 7, React.createElement(CommentIcon)),
    createSection('research', 'Research', 8, React.createElement(ResearchIcon)),
    createSection('history', 'History', 9, React.createElement(HistoryIcon)),
  ];

  // Return only enabled sections
  return allSections.filter(s => s.enabled);
}
